<template>
  <div class="ontag">
    <div class="ontag__content">
      <div>
        <span class="h1">OnTag Dashboard</span>
        <span
          v-if="isLoading"
          class="element--is-loading"></span>
      </div>
      <section class="status-boxes">
        <div
          class="status-boxes__box"
          v-for="(box, i) in printersOptions"
          :key="i">
          <div class="status-boxes__box--container">
            <div class="box__row box__type">
              <span class="box__type--type">
                {{ $t('OnTagDashboard.printer') }}:
              </span>
            </div>
            <div class="box__row box__status">
              <h3 class="box__status--name">
                {{ box.displayName }}
              </h3>
              <div
                v-if="box.status === true"
                class="box__status--container">
                <img
                  src="../../assets/images/oval-green.svg"
                  alt=""
                  class="box__status--image" />
                <span class="box__status--text">Ok</span>
              </div>
              <div
                v-else
                class="box__status--container">
                <img
                  src="../../assets/images/oval-red.svg"
                  alt=""
                  class="box__status--image" />
                <span class="box__status--text">
                  {{ $t('OnTagDashboard.error') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="status-boxes">
        <div
          class="status-boxes__box"
          v-for="(box, i) in clientOptions"
          :key="i">
          <div class="status-boxes__box--container">
            <div class="box__row box__type">
              <span class="box__type--type">
                {{ $t('OnTagDashboard.client') }}:
              </span>
            </div>
            <div class="box__row box__status">
              <div
                v-if="box.status === true"
                class="box__status--container">
                <img
                  src="../../assets/images/oval-green.svg"
                  alt=""
                  class="box__status--image" />
                <span class="box__status--text">Ok</span>
              </div>
              <client-error
                v-else
                :client="box" />
            </div>
          </div>
        </div>
      </section>
      <section class="printer-queue">
        <div class="printer-queue--container">
          <div class="select-printer">
            <p class="first-letter">{{ $t('printerQueue') }}:</p>
            <custom-select
              v-if="selectedPrinterIndex !== undefined"
              :required="false"
              @optionSelected="setSelectedPrinterIndex"
              :selected="selectedPrinterIndex"
              :options="printersOptions"
              class="select-printer__select" />
          </div>
          <div class="ontag--utilitites input-group--inline">
            <div class="search-wrapper ontag--utilitites__utility">
              <div class="input">
                <label
                  for="searchQuery"
                  class="input__label label__inline first-letter">
                  {{ $t('search') }}
                </label>
                <input
                  id="searchQuery"
                  class="input__input input__inline search-input"
                  @keyup.enter="getVariatedPrinterQueueResults"
                  v-model="searchQuery"
                  type="text"
                  name="searchQuery" />
              </div>
              <base-button
                class="search-button"
                :text="$t('search')"
                :button-size-small="true"
                @click="getVariatedPrinterQueueResults" />
            </div>
            <base-button
              class="button--icon-to-left ontag--utilitites__utility"
              :text="$t('deleteMarkedPrinterJobs')"
              :button-size-small="true"
              @click="toggleDeleteMarkedPrinterJobsModal"
              :disabled="allSelected.length === 0">
              <use-svg svg="trash-icon" />
            </base-button>
            <base-button
              class="button--icon-to-left ontag--utilitites__utility"
              :text="$t('emptyPrinterQueue')"
              :button-size-small="true"
              @click="toggleEmptyPrinterQueueModal">
              <use-svg svg="export-icon" />
            </base-button>
          </div>
          <list-table
            :items="printerQueue.rows"
            :headers="printerQueue.columns"
            @variationResults="getVariatedPrinterQueueResults"
            :use-cursor="false"
            @allSelected="getPrinterQueuesSelected"
            :page-size="100"
            ref="printerQueueListTable">
          </list-table>
          <results-pagination
            :first-row-on-page="pagination.firstRowOnPage"
            :last-row-on-page="pagination.lastRowOnPage"
            :row-count="pagination.rowCount"
            :page-size="pagination.pageSize"
            :page-count="pagination.pageCount"
            :current-page="pagination.currentPage"
            :next-page="getNextPage()"
            :prev-page="getPrevPage()"
            store="printerQueue"
            @currentPage="getVariatedPrinterQueueResults" />
          <br />
          <div>
            <h4>
              {{ $t('OnTagDashboard.statsForScorecard') }}
            </h4>
            <div class="flex">
              <div
                class="bar"
                v-if="paperPrintData">
                <span class="title">
                  {{ $t('OnTagDashboard.printedScorecards') }}
                </span>
                <bar-chart
                  :styles="{ height: '16rem' }"
                  :labels="[
                    $t('MonthsShort.Jan'),
                    $t('MonthsShort.Feb'),
                    $t('MonthsShort.Mar'),
                    $t('MonthsShort.Apr'),
                    $t('MonthsShort.May'),
                    $t('MonthsShort.Jun'),
                    $t('MonthsShort.Jul'),
                    $t('MonthsShort.Aug'),
                    $t('MonthsShort.Sep'),
                    $t('MonthsShort.Oct'),
                    $t('MonthsShort.Nov'),
                    $t('MonthsShort.Dec'),
                  ]"
                  :data="paperPrintData.data"
                  :tooltips="paperPrintData.tooltips" />
              </div>
              <div
                v-if="paperPrintData"
                class="totalCount">
                <h3>
                  {{ $t('OnTagDashboard.totalPrintedScorecards') }}
                </h3>
                <p>
                  <span> {{ $t('OnTagDashboard.currentYear') }}: </span>
                  <span>{{ paperPrintTotalCurrentY }} {{ $t('piece') }}</span>
                </p>
                <p>
                  <span> {{ $t('OnTagDashboard.previousYear') }}: </span>
                  <span>{{ paperPrintTotalLastY }} {{ $t('piece') }}</span>
                </p>
              </div>
              <div
                class="bar"
                v-if="digitalPrintData">
                <span class="title">
                  {{ $t('OnTagDashboard.digitalScorecards') }}
                </span>
                <bar-chart
                  :styles="{ height: '16rem' }"
                  :labels="[
                    $t('MonthsShort.Jan'),
                    $t('MonthsShort.Feb'),
                    $t('MonthsShort.Mar'),
                    $t('MonthsShort.Apr'),
                    $t('MonthsShort.May'),
                    $t('MonthsShort.Jun'),
                    $t('MonthsShort.Jul'),
                    $t('MonthsShort.Aug'),
                    $t('MonthsShort.Sep'),
                    $t('MonthsShort.Oct'),
                    $t('MonthsShort.Nov'),
                    $t('MonthsShort.Dec'),
                  ]"
                  :data="digitalPrintData.data"
                  :tooltips="digitalPrintData.tooltips" />
              </div>
              <div
                v-if="digitalPrintData"
                class="totalCount">
                <h3>{{ $t('OnTagDashboard.totalDigitalScorecards') }}</h3>
                <p>
                  <span> {{ $t('OnTagDashboard.currentYear') }}: </span>
                  <span>{{ digitalPrintTotalCurrentY }} {{ $t('piece') }}</span>
                </p>
                <p>
                  <span> {{ $t('OnTagDashboard.previousYear') }}: </span>
                  <span>{{ digitalPrintTotalLastY }} {{ $t('piece') }}</span>
                </p>
              </div>
            </div>
          </div>
          <modal-window :show="showDeleteMarkedPrinterJobsModal">
            <div class="modal__header">
              {{ $t('deleteMarkedPrinterJobs') }}
            </div>
            <div class="modal__content">
              <div>
                <h2>{{ $t('deleteMarkedPrinterJobsQuestion') }}?</h2>
              </div>
            </div>
            <div class="modal__content--actions">
              <base-button
                class="button--background-gray-dark"
                :text="$t('cancel')"
                @click="toggleDeleteMarkedPrinterJobsModal">
              </base-button>
              <base-button
                :text="$t('deleteMarkedPrinterJobs')"
                @click="deleteMarkedPrinterJobs" />
            </div>
          </modal-window>
          <modal-window :show="showEmptyPrinterQueueModal">
            <div class="modal__header">
              {{ $t('emptyPrinterQueue') }}
            </div>
            <div class="modal__content">
              <div>
                <h2>{{ $t('emptyPrinterQueueQuestion') }}?</h2>
              </div>
            </div>
            <div class="modal__content--actions">
              <base-button
                class="button--background-gray-dark"
                :text="$t('cancel')"
                @click="toggleEmptyPrinterQueueModal">
              </base-button>
              <base-button
                :text="$t('emptyPrinterQueue')"
                @click="emptyPrinterQueue" />
            </div>
          </modal-window>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ListTable from '@/components/list-table/list-table';
import BaseButton from '@/components/form-elements/base-button';
import UseSvg from '@/components/use-svg';
import CustomSelect from '@/components/form-elements/custom-select';
import ModalWindow from '@/components/modal/modal';
import ResultsPagination from '@/components/results-pagination';
import BarChart from '@/components/charts/ontag-bar-chart';
import LocalizationMixin from '@/mixins/localization-mixin';
import ClientError from '@/components/clients/client-error';

export default {
  name: 'Ontag',
  mixins: [LocalizationMixin],
  components: {
    ListTable,
    BaseButton,
    UseSvg,
    CustomSelect,
    ModalWindow,
    ResultsPagination,
    BarChart,
    ClientError,
  },
  data() {
    return {
      isLoading: true,
      searchQuery: '',
      currentPage: 0,
      active: undefined,
      showDeleteMarkedPrinterJobsModal: false,
      showEmptyPrinterQueueModal: false,
      allSelected: [],
      selectedPrinterIndex: undefined,
      sort: {},
      pageSizeOptions: [
        {
          value: '10',
          displayName: '10',
        },
        {
          value: '20',
          displayName: '20',
        },
        {
          value: '40',
          displayName: '40',
        },
        {
          value: '80',
          displayName: '80',
        },
      ],
      paperPrintData: false,
      paperPrintTotalCurrentY: false,
      paperPrintTotalLastY: false,
      digitalPrintData: false,
      digitalPrintTotalCurrentY: false,
      digitalPrintTotalLastY: false,
    };
  },
  computed: {
    printersOptions() {
      return this.$store.getters['printers/printersOptions'];
    },
    clientOptions() {
      return this.$store.getters['clients/clientOptions'];
    },
    clients() {
      return this.$store.getters['clients/clients'];
    },
    printerQueue() {
      return this.$store.getters['printerQueue/printerQueue'];
    },
    pagination() {
      return this.$store.getters['printerQueue/pagination'];
    },
    statistics() {
      return this.$store.getters['ontagStatistics/statistics'];
    },
    user() {
      return this.$store.getters['user/user'];
    },
  },
  created() {
    this.$store
      .dispatch('ontagStatistics/fetchStatistics')
      .then(() => {
        this.$store.dispatch('printers/fetchAllPrinters').then(() => {
          this.fetchPrinterQueue(
            this.$store.getters['printers/printersOptionsById']().value
          );
          this.selectedPrinterIndex = this.printersOptions.findIndex(
            options =>
              options.value ===
              this.$store.getters['printers/printersOptionsById']().value
          );
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.$store.dispatch('clients/fetchAllClients').then(() => {
          // this.selectedPageSize(this.pagination.pageSize)
        });
        this.$store.dispatch('ontagStatistics/fetchStatistics').then(() => {
          this.parse(this.$store.state.ontagStatistics.statistics);
        });
        this.$store.dispatch('printers/fetchPrintersOptions').then(() => {
          this.$store.dispatch('printers/fetchAllPrinters').then(() => {
            this.fetchPrinterQueue(
              this.$store.getters['printers/printersOptionsById']().value
            );
            this.selectedPrinterIndex = this.printersOptions.findIndex(
              options =>
                options.value ===
                this.$store.getters['printers/printersOptionsById']().value
            );
          });
        });
      });
  },
  methods: {
    fetchPrinterQueue(id) {
      this.$store.dispatch('printerQueue/fetchPrinterQueue', {
        id: id,
        pageSize: this.pagination.pageSize,
        page: 0,
      });
    },
    getVariatedPrinterQueueResults(value) {
      if (value.sort) this.sort = value.sort;

      if (value.filter) {
        let checked = value.filter.options.filter(f => f.checked);
        if (
          checked.length < value.filter.options.length &&
          checked.length > 0
        ) {
          if (checked[0].value === '1') {
            this.active = true;
          } else {
            this.active = false;
          }
        } else {
          this.active = undefined;
        }
      }

      if (value.pageSize) this.pagination.pageSize = value.pageSize;

      if (value.page) this.currentPage = value.page;

      this.$store.dispatch('printerQueue/fetchPrinterQueue', {
        id: this.printersOptions[this.selectedPrinterIndex].value,
        sortColumn: this.sort.id,
        sortDirection: this.sort.direction,
        search: this.searchQuery.length > 0 ? this.searchQuery : undefined,
        'Filter.status': this.active,
        pageSize: this.pagination.pageSize,
        page: this.searchQuery.length > 0 ? '1' : this.currentPage,
      });
    },
    deleteMarkedPrinterJobs() {
      this.$store
        .dispatch('printerQueue/deleteMarkedPrinterQueues', {
          ids: this.allSelected,
        })
        .then(() => {
          this.toggleDeleteMarkedPrinterJobsModal();
          this.allSelected = [];
          this.currentPage = 1;
          if (this.$refs && this.$refs.printerQueueListTable)
            this.$refs.printerQueueListTable.resetSelectAll();
          this.fetchPrinterQueue(
            this.printersOptions[this.selectedPrinterIndex].value
          );
        });
    },
    emptyPrinterQueue() {
      this.$store
        .dispatch('printerQueue/deletePrinterQueue', {
          id: this.$route.params.id,
        })
        .then(() => {
          this.toggleEmptyPrinterQueueModal();
        });
    },
    toggleDeleteMarkedPrinterJobsModal() {
      this.showDeleteMarkedPrinterJobsModal =
        !this.showDeleteMarkedPrinterJobsModal;
    },
    toggleEmptyPrinterQueueModal() {
      this.showEmptyPrinterQueueModal = !this.showEmptyPrinterQueueModal;
    },
    getPrinterQueuesSelected(value) {
      this.allSelected = value;
    },
    setSelectedPrinterIndex(printer) {
      this.selectedPrinterIndex = this.printersOptions.findIndex(
        options => options.value === printer.value
      );
      this.fetchPrinterQueue(printer.value);
    },
    getPrevPage() {
      return this.$store.getters['users/previousPage'];
    },
    getNextPage() {
      return this.$store.getters['users/nextPage'];
    },
    setPageSize(size) {
      let pageSize = {
        pageSize: size.value,
      };
      this.currentPage = 1;
      this.getVariatedPrinterQueueResults(pageSize);
    },
    selectedPageSize(size) {
      let selectedSize = this.pageSizeOptions.findIndex(
        o => parseInt(o.value) === size
      );
      this.selectedIndexPageSize = selectedSize;
    },
    parse2(fields, data) {
      var d = {};
      var t = {};
      d.currentYear = fields.map(function (y) {
        var v = data.currentYearData.find(x => x.name === y);
        return v ? v.value : 0;
      });
      t.currentYear = fields.map(function (y) {
        var v = data.currentYearData.find(x => x.name === y);
        return v ? v.displayValue : '';
      });
      if (data.lastYearData) {
        d.lastYear = fields.map(function (y) {
          var v = data.lastYearData.find(x => x.name === y);
          return v ? v.value : 0;
        });
        t.lastYear = fields.map(function (y) {
          var v = data.lastYearData.find(x => x.name === y);
          return v ? v.displayValue : '';
        });
      }
      return { data: d, tooltips: t };
    },
    parse(statistics) {
      this.paperPrintData = this.parse2(
        ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
        statistics.paperPrintData
      );
      this.paperPrintTotalCurrentY =
        this.statistics.paperPrintTotalData.currentYearData[0].value;
      this.paperPrintTotalLastY =
        this.statistics.paperPrintTotalData.lastYearData[0].value;
      this.digitalPrintData = this.parse2(
        ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
        statistics.digitalPrintData
      );
      this.digitalPrintTotalCurrentY =
        this.statistics.digitalPrintTotalData.currentYearData[0].value;
      this.digitalPrintTotalLastY =
        this.statistics.digitalPrintTotalData.lastYearData[0].value;
    },
  },
};
</script>
<style lang="scss">
$color-white: #ffffff;
$color-gray: #f0f0f0;
$color-blue-dark: #0399e4;
$color-blue-light: #e3f2fd;
$color-dark: #212121;
$font-size-h1: 2rem;
$font-size-tiny: 0.875rem;
$font-size-small: 1rem;
$font-size-title: 1.1rem;
$spacing: 1rem;

.h1 {
  display: inline-block;
  font-size: $font-size-h1;
  font-weight: bold;
  margin: 1.5rem 0;
  color: $color-dark;
}

.element--is-loading {
  margin: 1.5rem 0 0 0.5rem;
  display: inline-block;
  width: 2rem;
  height: 2rem;
  background: url('data:image/svg+xml,...') no-repeat center center / 2rem 2rem;
}

.ontag {
  flex-grow: 1;
  width: 100%;
  @include media('small') {
    width: calc(100% / 2 - 1rem);
  }
  @include media('large') {
    width: calc(100% / 3 - 2rem);
  }
}

.ontag__content {
  background-color: $color-white;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  padding: $spacing;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
  &.dashboard {
    background-color: transparent;
    border-radius: 0;
  }
}

.status-boxes {
  display: flex;
  margin-bottom: $spacing;
  flex-wrap: wrap;
}

.status-boxes__box {
  flex-direction: column;
  margin-right: $spacing;
  margin-bottom: $spacing;
  background-color: $color-white;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
  padding: $spacing;
  transition: transform 0.3s ease;
  &:hover {
    transform: translateY(-0.5rem);
  }
}

.box__row {
  display: flex;
  margin-bottom: 0.5rem;
}

.box__type--type {
  font-size: $font-size-tiny;
  padding-top: 0.5rem;
  color: $color-dark;
}

.box__status {
  display: flex;
  align-items: center;
}

.box__status--name {
  margin-right: 1rem;
  font-weight: bold;
  color: $color-dark;
}

.box__status--container {
  position: relative;
}

.box__status--image {
  margin-top: 0.3rem;
  width: 3rem;
  height: 3rem;
}

.box__status--text {
  font-size: $font-size-tiny;
  color: $color-dark;
}

.printer-queue {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: $spacing;
}

.printer-queue--container {
  background-color: $color-white;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
  padding: $spacing;
  width: 100%;
}

.select-printer {
  display: flex;
  align-items: center;
  margin-bottom: $spacing;
}

.select-printer__select {
  min-width: 10rem;
  margin-left: 1rem;
}

.search-wrapper {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.ontag--utilitites {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: $spacing;
}

.ontag--utilitites__utility {
  margin-right: 0.5rem;
  &:last-child {
    margin-right: 0;
  }
}

.flex {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
}

.bar {
  width: 100%;
  max-width: 48%;
  background-color: $color-white;
  border: 1px solid $color-gray;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
  margin: 0.75rem 0;
  padding: $spacing;
  transition: transform 0.3s ease;
  &:hover {
    transform: translateY(-0.5rem);
  }
}

.totalCount {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: $spacing;
  background-color: $color-white;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
  margin: $spacing 0;

  > p {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 20rem;
    margin: 0.5rem 0;

    span:nth-child(2n) {
      color: $color-blue-dark;
    }
  }
}

.title {
  font-size: $font-size-title;
  color: $color-dark;
  margin-bottom: 0.5rem;
}

.modal__header {
  font-size: $font-size-title;
  font-weight: bold;
  color: $color-dark;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid $color-gray;
  margin-bottom: $spacing;
}

.modal__content {
  padding: $spacing 0;
  color: $color-dark;
}

.modal__content--actions {
  display: flex;
  justify-content: flex-end;
  gap: $spacing;

  button {
    padding: 0.5rem 1rem;
    border-radius: 0.25rem;
    font-size: $font-size-small;
    cursor: pointer;
    border: none;
    transition: background-color 0.3s ease;
    &:hover {
      background-color: darken($color-blue-light, 10%);
    }
  }

  .button--background-gray-dark {
    background-color: $color-gray;
    color: $color-dark;
  }
}

.base-button {
  background-color: $color-blue-dark;
  color: $color-white;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: $font-size-small;
  cursor: pointer;
  border: none;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: darken($color-blue-dark, 10%);
  }
}
</style>
